import React, { useContext, useEffect, useState } from "react";
import { Box, Select } from "theme-ui";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { getNotificationPrefs, setNotificationPrefs, getClientNotificationPrefs, setClientNotificationPrefs } from "api/QuoteExpiration";

const ALERT_FREQUENCY_OPTIONS = [
  { value: "off", label: "Off" },
  { value: "immediate", label: "As they happen" },
  { value: "daily", label: "Daily summary" },
  { value: "weekly", label: "Weekly summary" },
  { value: "monthly", label: "Monthly summary" },
];

const DIGEST_FREQUENCY_OPTIONS = [
  { value: "off", label: "Off" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
];

const NOTIFICATIONS = [
  {
    key: "quoteExpirationFrequency",
    title: "Quote Expiration Alerts",
    description: "Get notified when quotes are approaching their expiration date.",
    options: ALERT_FREQUENCY_OPTIONS,
  },
  {
    key: "rfqExpirationFrequency",
    title: "RFQ Expiration Alerts",
    description: "Get notified when quote requests are approaching expiration.",
    options: ALERT_FREQUENCY_OPTIONS,
  },
  {
    key: "priceLockFrequency",
    title: "Price Lock Alerts",
    description: "Get notified when a secured price lock is approaching its expiration date.",
    options: ALERT_FREQUENCY_OPTIONS,
  },
  {
    key: "digestFrequency",
    title: "Expiration Summary",
    description: "A scheduled overview of all quotes and RFQs that are expiring soon.",
    options: DIGEST_FREQUENCY_OPTIONS,
  },
];

const FrequencyRow: React.FC<{
  title: string;
  description: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  isInherited?: boolean;
  onReset?: () => void;
}> = ({ title, description, value, options, onChange, isInherited, onReset }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", backgroundColor: "#FAFAFA", borderRadius: "8px" }}>
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
        <span style={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "#2D3748" }}>
          {title}
        </span>
        {isInherited && (
          <span style={{ fontFamily: "Poppins", fontSize: "10px", color: "#A0AEC0", backgroundColor: "#EDF2F7", padding: "2px 6px", borderRadius: "4px" }}>
            Default
          </span>
        )}
        {!isInherited && onReset && (
          <button
            onClick={onReset}
            style={{
              fontFamily: "Poppins",
              fontSize: "10px",
              color: "#3182CE",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Reset to default
          </button>
        )}
      </div>
      <div style={{ fontFamily: "Poppins", fontSize: "12px", color: "#718096", lineHeight: "1.4" }}>
        {description}
      </div>
    </div>
    <div style={{ marginLeft: "16px", flexShrink: 0 }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ fontFamily: "Poppins", fontSize: "13px", minWidth: "160px" }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </Select>
    </div>
  </div>
);

export default function NotificationSettings() {
  const { user } = useContext<any>(UserContext);
  const toast = useToast();

  const isAdmin = user?.accessLevel === "isSuper" || user?.accessLevel === "isCompany";
  const isClient = user?.accessLevel === "isClient" || user?.accessLevel === "isClientAdmin";
  const companyId = user?.company?.id;
  const userClientId = user?.client?.id;

  const [prefs, setPrefs] = useState<Record<string, string>>({});
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  // Load preferences
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (isAdmin && companyId) {
          // Admin prefs from company_data via notification endpoint
          const res = await getNotificationPrefs(companyId);
          if (res?.quoteExpirationFrequency !== undefined) {
            setPrefs(res);
            // Admin prefs don't have an overrides concept — they're always explicit
            setOverrides({
              quoteExpirationFrequency: true,
              rfqExpirationFrequency: true,
              priceLockFrequency: true,
              digestFrequency: true,
            });
          }
        } else if (isClient && userClientId) {
          // Client prefs from client_data via client notification endpoint
          const res = await getClientNotificationPrefs(userClientId);
          if (res) {
            setPrefs({
              quoteExpirationFrequency: res.quoteExpirationFrequency || "off",
              rfqExpirationFrequency: res.rfqExpirationFrequency || "off",
              priceLockFrequency: res.priceLockFrequency || "off",
              digestFrequency: res.digestFrequency || "off",
            });
            setOverrides(res.overrides || {});
          }
        }
      } catch (e: any) {
        console.error("Failed to load notification prefs:", e);
      }
      setLoading(false);
    };
    load();
  }, [isAdmin, isClient, companyId, userClientId]);

  const handleChange = async (key: string, value: string) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);

    if (isAdmin && companyId) {
      const res = await setNotificationPrefs(companyId, updated);
      if (res?.success) {
        toast.add("Preferences saved.", "var(--theme-ui-colors-success)");
      } else {
        toast.add(res?.message || "Failed to save.", "var(--theme-ui-colors-danger)");
      }
    } else if (isClient && userClientId) {
      const updatedOverrides = { ...overrides, [key]: true };
      setOverrides(updatedOverrides);

      // Build payload with only explicitly overridden fields
      const payload: Record<string, string> = {};
      for (const notif of NOTIFICATIONS) {
        if (updatedOverrides[notif.key]) {
          payload[notif.key] = notif.key === key ? value : (prefs[notif.key] || "off");
        }
      }
      const res = await setClientNotificationPrefs(userClientId, payload);
      if (res?.success) {
        toast.add("Preferences saved.", "var(--theme-ui-colors-success)");
      } else {
        toast.add(res?.message || "Failed to save.", "var(--theme-ui-colors-danger)");
      }
    }
  };

  const handleReset = async (key: string) => {
    if (!isClient || !userClientId) return;

    const updatedOverrides = { ...overrides };
    delete updatedOverrides[key];
    setOverrides(updatedOverrides);

    // Re-save without this key so it inherits the default
    const payload: Record<string, string> = {};
    for (const notif of NOTIFICATIONS) {
      if (updatedOverrides[notif.key]) {
        payload[notif.key] = prefs[notif.key] || "off";
      }
    }
    const res = await setClientNotificationPrefs(userClientId, payload);
    if (res?.success) {
      // Reload to get the inherited default value
      const refreshed = await getClientNotificationPrefs(userClientId);
      if (refreshed) {
        setPrefs({
          quoteExpirationFrequency: refreshed.quoteExpirationFrequency || "off",
          rfqExpirationFrequency: refreshed.rfqExpirationFrequency || "off",
          priceLockFrequency: refreshed.priceLockFrequency || "off",
          digestFrequency: refreshed.digestFrequency || "off",
        });
        setOverrides(refreshed.overrides || {});
      }
      toast.add("Reset to default.", "var(--theme-ui-colors-success)");
    }
  };

  if (loading) {
    return (
      <Box sx={{ fontFamily: "Poppins", fontSize: "13px", color: "base_500", padding: "20px" }}>
        Loading notification preferences...
      </Box>
    );
  }

  return (
    <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", fontWeight: 600, color: "base_800", marginBottom: "4px" }}>
        {isAdmin ? "My Notification Preferences" : "My Notifications"}
      </Box>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "base_500", marginBottom: "24px" }}>
        {isAdmin
          ? "Choose how and when you receive email notifications about expiring quotes and price locks."
          : "Choose how and when you receive email notifications. Your defaults are set by your service provider \u2014 adjust any below to suit your preference."
        }
      </Box>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {NOTIFICATIONS.map((notif) => (
          <FrequencyRow
            key={notif.key}
            title={notif.title}
            description={notif.description}
            value={prefs[notif.key] || "off"}
            options={notif.options}
            onChange={(value) => handleChange(notif.key, value)}
            isInherited={isClient ? !overrides[notif.key] : false}
            onReset={isClient && overrides[notif.key] ? () => handleReset(notif.key) : undefined}
          />
        ))}
      </div>

      <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginTop: "20px", fontStyle: "italic" }}>
        Changes save automatically.
        {isAdmin && " System-wide notification defaults for new users are configured on the Expiration Settings page."}
      </Box>
    </Box>
  );
}
